import type { ButtonProps } from "primereact/button";
import type { ConfirmDialogProps as PrimeConfirmDialogProps } from "primereact/confirmdialog";
import { useRef, useState } from "react";

import { TEST_ID_PREFIX_ATTRIBUTE } from "../confirm-dialog.defaults";
import type { GatedAccept } from "../confirm-dialog.defaults";
import { CONFIRM_ERROR_FALLBACK } from "../messages";
import type { ConfirmFooterProps, ConfirmFooterViewModel } from "./types-confirm-footer";

const errorMessage = (error: unknown): string =>
  error instanceof Error && error.message ? error.message : CONFIRM_ERROR_FALLBACK;

/**
 * ViewModel do rodapé do ConfirmDialog: pendente/erro do `accept` da tela e guard de duplo clique; sem JSX.
 * O estado vive no rodapé, que o Prime desmonta ao fechar: reabrir começa sem o erro anterior.
 */
export function useConfirmFooter({
  options,
  onPendingChange,
}: ConfirmFooterProps): ConfirmFooterViewModel {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pendingRef = useRef(false);
  const current = options.props as PrimeConfirmDialogProps & Record<string, unknown>;
  const gated = current.accept as GatedAccept | undefined;
  const defaultFocus = current.defaultFocus;
  const testIdPrefix = current[TEST_ID_PREFIX_ATTRIBUTE] as string | undefined;

  const settle = (value: boolean) => {
    pendingRef.current = value;
    setPending(value);
    onPendingChange(value);
  };

  return {
    pending,
    error,
    errorTestId: testIdPrefix ? `${testIdPrefix}-error` : undefined,
    testIdPrefix,
    destructive: current["data-variant"] === "destructive",
    acceptLabel: options.acceptLabel,
    rejectLabel: options.rejectLabel,
    acceptIcon: current.acceptIcon as ButtonProps["icon"],
    rejectIcon: current.rejectIcon as ButtonProps["icon"],
    acceptClassName: current.acceptClassName,
    rejectClassName: current.rejectClassName,
    acceptAutoFocus: defaultFocus === undefined || defaultFocus === "accept",
    rejectAutoFocus: defaultFocus === "reject",
    reject: options.reject,
    accept: async () => {
      if (pendingRef.current) return;
      setError(null);
      settle(true);
      let accepted = false;
      try {
        await gated?.run?.();
        accepted = true;
      } catch (failure) {
        setError(errorMessage(failure));
      }
      settle(false);
      // Fora do `try`: um erro do `onHide` da tela não vira mensagem do diálogo.
      if (accepted && gated) gated.skip = true;
      try {
        if (accepted) options.accept();
      } finally {
        if (gated) gated.skip = false;
      }
    },
  };
}
